import { useMemo, type ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_CHROME } from "@/shared/ui/chart/public";
import { CHART_LABEL } from "./chartTokens";
import { CompareAxisTick } from "./CompareAxisTick";
import type { EvalResultSeries } from "./evalCompareModel";
import { formatPercentStat, mergeOutcomes, outcomeCountKey } from "./evalCompareSummaryModel";
import styles from "./EvaluationComparisonViewer.module.css";
import {
  MergedSeriesGradient,
  mergedSeriesPaint,
  useMergedSeriesGradientId,
} from "./MergedSeriesGradient";

export function OutcomeCompareChart({ items }: { items: readonly EvalResultSeries[] }): ReactNode {
  const mergedGradientId = useMergedSeriesGradientId();
  const data = useMemo(() => mergeOutcomes(items), [items]);
  if (data.length === 0) return null;
  return (
    <div className={styles.chartCard}>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ top: 20, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <MergedSeriesGradient id={mergedGradientId} />
          </defs>
          <CartesianGrid stroke={CHART_CHROME.grid} vertical={false} />
          <XAxis
            dataKey="label"
            height={44}
            tickLine={false}
            axisLine={false}
            tick={<CompareAxisTick />}
          />
          <YAxis
            width={44}
            stroke={CHART_CHROME.axis}
            fontSize={11}
            domain={[0, 1]}
            tickFormatter={(tick: number) => formatPercentStat(tick)}
          />
          <Tooltip
            cursor={{ fill: "transparent" }}
            contentStyle={{
              background: CHART_CHROME.tooltipBg,
              borderColor: CHART_CHROME.tooltipBorder,
            }}
            // 막대 높이는 비율이지만 사람이 확인하는 값은 Episode 수다. 그래서 둘을 함께 반환한다.
            formatter={(value: number, name: string, entry: { dataKey?: string | number }) => {
              const countKey = outcomeCountKey(String(entry.dataKey ?? ""));
              const point = (entry as { payload?: Record<string, number | string> }).payload ?? {};
              return [`${point[countKey] ?? 0}개 · ${formatPercentStat(value)}`, name];
            }}
          />
          {items.map((item) => (
            <Bar
              key={item.key}
              dataKey={item.key}
              name={item.label}
              fill={mergedSeriesPaint(item.key, item.color, mergedGradientId)}
              radius={[3, 3, 0, 0]}
            >
              <LabelList
                dataKey={outcomeCountKey(item.key)}
                position="top"
                fill={CHART_LABEL}
                fontSize={11}
                fontWeight={600}
              />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
